import clsx from 'clsx';
import { Fragment, useEffect, useRef } from 'react';

import { Tab } from './tab.js';

import type { LinkProps } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { TabProps } from './tab.js';

export type TabDef = {
	id: string;
	label: string;
	labelAdornEnd?: ReactNode;
	/** If set, the tab button/link will receive this as its `data-testid` attribute. */
	testId?: string;
	/** If set, the tab will render as a TanStack Router <Link> component. */
	linkProps?: Omit<LinkProps, 'children'>;
	/** If true, the tab renders muted and non-interactive. */
	disabled?: boolean;
};

export type TabsProps<TabDefs extends TabDef[], TabIds = TabDefs[number]['id']> = {
	tabs: TabDefs;
	selectedTab: TabIds | null;
	onSelectedTabChange?: (tab: TabIds) => void;
	renderTab?: (props: TabProps) => ReactNode;
	/** Custom className for the container */
	className?: string;
};

export function Tabs<TabDefs extends TabDef[]>({
	selectedTab,
	onSelectedTabChange,
	tabs,
	renderTab = (props: TabProps) => <Tab {...props} />,
	className,
}: TabsProps<TabDefs>) {
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		const handleWheel = (e: WheelEvent) => {
			if (container.scrollWidth <= container.clientWidth) {
				return;
			}
			// A trackpad swipe or tilt-wheel already reports its motion as deltaX and scrolls the
			// container natively in the direction the OS's scroll-direction setting dictates. Only a
			// plain vertical wheel (deltaY-dominant, deltaX ~0) needs remapping onto the horizontal axis.
			if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
				return;
			}
			e.preventDefault();
			container.scrollLeft += e.deltaY;
		};

		container.addEventListener('wheel', handleWheel, { passive: false });
		return () => container.removeEventListener('wheel', handleWheel);
	}, []);

	return (
		<div
			ref={containerRef}
			className={clsx('-mx-1 -mb-px grid min-w-0 shrink-0 scrollbar-none overflow-x-auto overflow-y-hidden', className)}
		>
			<div className='flex contain-paint'>
				{tabs.map((tab) => (
					<Fragment key={tab.id}>
						{renderTab({
							id: tab.id,
							isSelected: selectedTab === tab.id,
							label: tab.label,
							labelAdornEnd: tab.labelAdornEnd,
							testId: tab.testId,
							linkProps: tab.linkProps,
							disabled: tab.disabled,
							onClick: () => {
								if (!tab.disabled) {
									onSelectedTabChange?.(tab.id);
								}
							},
						})}
					</Fragment>
				))}
			</div>
		</div>
	);
}
